import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sprite } from '@pixi/react';
import { gsap } from 'gsap';
import { observer } from 'mobx-react-lite';
import { getGameImages } from '../../../core/helpers/getGameImages';
import { gameService } from '../../../services/gameService';
import { textureChangeFrame } from '../../../core/helpers/textureChangeFrame';
import { gameStore } from '../../../store/gameStore';
import { gameDrawer } from '../../../services/gameDrawer';
import { bettingPartStore } from '../../../store/bettingPartStore';
import { gambleStore } from '../../../store/gambleStore';
import IconBackGround from '../../common/iconBackGround';
import { isMobile } from '../../../core/helpers/isMobile';
import { userInfoStore } from '../../../store/userInfoStore';

const SpinButton = observer(({ x = 0, y = 0 }) => {
  const [isSpinAnimationEnd, setIsSpinAnimationEnd] = useState(false);
  const { isSpinStart } = gameStore;
  const { isReplay } = userInfoStore;
  const { isShowGamblePopup } = gambleStore;
  const { isAutoBetStart } = bettingPartStore;
  const {
    spinIconBgSelected,
    spinIconBgDefault,
    spinIconBgPressed,
    spinDefault,
    spinPressed,
    spinStop,
  } = useMemo(() => {
    return getGameImages();
  }, []);
  const spinButtonContent = useRef(null);

  const handlePointerDown = useCallback(() => {
    if (spinButtonContent.current && !isSpinStart && !isAutoBetStart) {
      gsap.to(spinButtonContent.current, {
        rotation: Math.PI * 0.5,
        duration: 0.2,
        delay: 0.1,
        ease: 'none',
        onComplete: () => {
          setTimeout(() => {
            setIsSpinAnimationEnd(true);
          }, 200);
          if (isSpinStart) {
            textureChangeFrame(spinButtonContent.current, spinStop);
          } else {
            textureChangeFrame(spinButtonContent.current, spinPressed);
          }
          spinButtonContent.current.rotation = 0;
        },
      });
    }
  }, [spinButtonContent, spinPressed, isSpinStart, spinStop, isAutoBetStart]);

  const handleSpin = useCallback(() => {
    if (isSpinStart || isAutoBetStart) {
      gameDrawer.forceStopAnimation();
    } else {
      gameService.doSpin();
    }
  }, [isSpinStart, isAutoBetStart]);

  const handleKeyDown = useCallback(
    (e) => {
      if ((e.code === 'Space' || e.code === 'Enter') && !isReplay) {
        e.preventDefault();
        handlePointerDown();
        handleSpin();
      }
    },
    [isReplay, handleSpin, handlePointerDown]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (!isAutoBetStart && !isSpinStart && spinButtonContent.current && !isSpinAnimationEnd) {
      textureChangeFrame(spinButtonContent.current, spinDefault);
      setIsSpinAnimationEnd(false);
    }
    if ((isSpinStart && isSpinAnimationEnd) || isAutoBetStart) {
      textureChangeFrame(spinButtonContent.current, spinStop);
      setIsSpinAnimationEnd(false);
    }
  }, [isSpinStart, spinButtonContent, spinDefault, isSpinAnimationEnd, spinStop, isAutoBetStart]);

  useEffect(() => {
    if (isAutoBetStart) textureChangeFrame(spinButtonContent.current, spinStop);
  }, [isAutoBetStart, spinButtonContent, spinStop]);

  const additionalBackGroundImages = useMemo(() => {
    return {
      iconBgDefault: spinIconBgDefault,
      iconBgDisabled: spinIconBgDefault,
      iconBgHovered: spinIconBgDefault,
      iconBgPressed: spinIconBgPressed,
      iconBgSelected: spinIconBgSelected,
    };
  }, [spinIconBgDefault, spinIconBgPressed, spinIconBgSelected]);

  if (isShowGamblePopup) {
    return null;
  }
  return (
    <IconBackGround
      x={x}
      y={y}
      isDisabled={isReplay}
      additionalBackGroundImages={additionalBackGroundImages}
      handleSelect={handleSpin}
      pointerDown={handlePointerDown}
    >
      <Sprite
        ref={spinButtonContent}
        texture={spinDefault}
        scale={isMobile() ? 0.77 : 0.5}
        anchor={0.5}
      />
    </IconBackGround>
  );
});

export default SpinButton;
